import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useGetStudentByIdQuery } from "../api/studentApi";
import { API_CONFIG } from "../../../../core/config";
import { useStudent } from "../hooks/useStudent";
import { showToast } from "../../../../core/utils/alert";
import Webcam from "react-webcam";
import ReactCrop, { Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

// Component for uploading student documents
const StudentDocuments = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const { data: studentResponse, isLoading } = useGetStudentByIdQuery({
    id: id || "",
  });
  const student = studentResponse?.data?.student;
  const { uploadStudentPhoto, uploadStudentDobDocument } = useStudent();

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [dobFile, setDobFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();

  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [hasCameraSupport, setHasCameraSupport] = useState(false);
  const webcamRef = useRef<Webcam | null>(null);

  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState("");

  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: "%",
    width: 80,
    x: 0,
    y: 0,
    height: 0,
  });
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const studentPhotoUrl = student?.docs?.photo
    ? `${API_CONFIG.BASE_URL}students/get-thumbnail/${
        student.docs.photo
      }?v=${Date.now()}`
    : null;

  useEffect(() => {
    const checkCameraSupport = async () => {
      if (navigator.mediaDevices) {
        setHasCameraSupport(true);
        try {
          await navigator.mediaDevices.getUserMedia({ video: true });
          const devices = await navigator.mediaDevices.enumerateDevices();
          setVideoDevices(
            devices.filter((device) => device.kind === "videoinput")
          );
        } catch (error) {
          console.error("Error accessing camera:", error);
        }
      }
    };
    checkCameraSupport();
  }, []);

  useEffect(() => {
    if (!completedCrop || !imgRef.current || !previewCanvasRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      console.error("Failed to get canvas context");
      return;
    }

    const pixelRatio = window.devicePixelRatio;
    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );
  }, [completedCrop]);

  const handleFileChange = (
    field: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (field === "photo") {
        const reader = new FileReader();
        reader.onload = () => {
          setImageToCrop(reader.result as string);
          setShowCropper(true);
        };
        reader.readAsDataURL(file);
      }
      if (field === "dob") setDobFile(file);
    }
  };

  const startCamera = async () => {
    try {
      const constraints = {
        video: selectedVideoDevice
          ? { deviceId: selectedVideoDevice }
          : { facingMode: "user" },
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(
        constraints
      );
      setStream(mediaStream);
      setShowCamera(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      showToast({
        title: "Camera Error",
        text: "Unable to access camera. Please check permissions.",
        icon: "error",
      });
    }
  };

  const stopCamera = () => {
    if (stream) {
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setImageToCrop(imageSrc);
        stopCamera();
        setShowCropper(true);
      }
    }
  };

  const getCroppedImg = (canvas: HTMLCanvasElement): Promise<File> => {
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error("Canvas is empty");
            return;
          }
          const croppedFile = new File([blob], "cropped-image.jpeg", {
            type: "image/jpeg",
          });
          resolve(croppedFile);
        },
        "image/jpeg",
        1
      );
    });
  };

  const saveCroppedImage = async () => {
    if (completedCrop && previewCanvasRef.current) {
      try {
        const croppedFile = await getCroppedImg(previewCanvasRef.current);
        setPhotoFile(croppedFile);
        setShowCropper(false);
        setImageToCrop(null);
      } catch (e) {
        console.error("Error creating cropped file:", e);
        showToast({
          title: "Error",
          text: "Failed to crop the image",
          icon: "error",
        });
      }
    }
  };

  const cancelCropping = () => {
    setShowCropper(false);
    setImageToCrop(null);
  };

  const handleUpload = async () => {
    if (!id) return;

    setIsUploading(true);
    try {
      if (photoFile) {
        const photoResponse = await uploadStudentPhoto(id, photoFile);
        if (!photoResponse.success) {
          console.log("photoResponse", photoResponse);
        }
      }

      if (dobFile) {
        const dobResponse = await uploadStudentDobDocument(id, dobFile);
        if (!dobResponse.success) {
          console.log("dobResponse", dobResponse);
        }
      }
      showToast({
        title: "Success",
        text: "Documents uploaded successfully",
        icon: "success",
      });
      navigate(`${routes.studentList}?refresh=${Date.now()}`);
    } catch (error) {
      showToast({
        title: "Error",
        text: error instanceof Error ? error.message : "Document upload failed",
        icon: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Upload Student Documents</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.studentList}>Students</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Upload Student Documents
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-xl-8 col-md-10">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <div className="mb-4">
                  <h5 className="card-title">Required Documents</h5>
                  <p className="text-muted">
                    Upload student's photo and date of birth proof
                  </p>
                </div>

                {isLoading ? (
                  <div className="text-center">Loading...</div>
                ) : (
                  <>
                    {showCropper && imageToCrop ? (
                      <div className="cropper-container border rounded p-3 mb-4">
                        <h6 className="mb-3">Crop Student Photo</h6>
                        <div className="crop-container mb-3">
                          <ReactCrop
                            crop={crop}
                            onChange={(c) => setCrop(c)}
                            onComplete={(c) => setCompletedCrop(c)}
                            aspect={1}
                          >
                            <img
                              ref={imgRef}
                              src={imageToCrop!}
                              alt="Crop me"
                              style={{ maxWidth: "100%" }}
                            />
                          </ReactCrop>
                        </div>

                        <div className="d-none">
                          <canvas
                            ref={previewCanvasRef}
                            style={{
                              width: completedCrop?.width ?? 0,
                              height: completedCrop?.height ?? 0,
                            }}
                          />
                        </div>

                        <div className="crop-preview mb-3">
                          <h6>Preview:</h6>
                          {completedCrop && (
                            <div
                              className="preview-container border rounded"
                              style={{
                                width: Math.min(completedCrop?.width, 200),
                                height: Math.min(completedCrop?.height, 200),
                                overflow: "hidden",
                              }}
                            >
                              <canvas
                                ref={previewCanvasRef}
                                style={{
                                  maxWidth: "100%",
                                  display: "block",
                                }}
                              />
                            </div>
                          )}
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={saveCroppedImage}
                            disabled={
                              !completedCrop?.width || !completedCrop?.height
                            }
                          >
                            Save Cropped Image
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={cancelCropping}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-4">
                        <label className="form-label d-block">
                          Student Photo (JPEG/PNG)
                        </label>
                        <div className="custom-file-container border rounded p-3">
                          {showCamera ? (
                            <div className="camera-container">
                              <div className="video-wrapper mb-3">
                                <Webcam
                                  ref={webcamRef}
                                  audio={false}
                                  screenshotFormat="image/jpeg"
                                  videoConstraints={
                                    selectedVideoDevice
                                      ? { deviceId: selectedVideoDevice }
                                      : { facingMode: "user" }
                                  }
                                  style={{
                                    maxWidth: "100%",
                                    borderRadius: "8px",
                                  }}
                                />
                              </div>
                              {videoDevices.length > 0 && (
                                <select
                                  className="form-select mb-3"
                                  value={selectedVideoDevice}
                                  onChange={(e) =>
                                    setSelectedVideoDevice(e.target.value)
                                  }
                                >
                                  <option value="">Default Camera</option>
                                  {videoDevices.map((device) => (
                                    <option
                                      key={device.deviceId}
                                      value={device.deviceId}
                                    >
                                      {device.label ||
                                        `Camera ${
                                          videoDevices.indexOf(device) + 1
                                        }`}
                                    </option>
                                  ))}
                                </select>
                              )}
                              <div className="d-flex gap-2 mb-3">
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={capturePhoto}
                                >
                                  <i className="ti ti-camera me-1"></i>
                                  Capture Photo
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={stopCamera}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="d-flex gap-2 mb-3">
                                <div className="flex-grow-1">
                                  <input
                                    type="file"
                                    className="form-control"
                                    accept="image/*"
                                    onChange={(e) =>
                                      handleFileChange("photo", e)
                                    }
                                    disabled={isUploading}
                                  />
                                </div>
                                {hasCameraSupport && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={startCamera}
                                    disabled={isUploading}
                                  >
                                    <i className="ti ti-camera me-1"></i>
                                    Take Photo
                                  </button>
                                )}
                              </div>

                              {(photoFile || studentPhotoUrl) && (
                                <div className="mt-3">
                                  {photoFile ? (
                                    <img
                                      src={URL.createObjectURL(photoFile)}
                                      alt="Preview"
                                      className="img-thumbnail"
                                      style={{
                                        maxWidth: "200px",
                                        maxHeight: "200px",
                                      }}
                                    />
                                  ) : (
                                    <img
                                      src={studentPhotoUrl || ""}
                                      alt="Current Student"
                                      className="img-thumbnail"
                                      style={{
                                        maxWidth: "200px",
                                        maxHeight: "200px",
                                      }}
                                    />
                                  )}
                                  {photoFile && (
                                    <div className="mt-2 text-primary">
                                      <i className="ti ti-file-text me-2"></i>
                                      {photoFile.name}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mb-4">
                      <label className="form-label d-block">
                        Date of Birth Proof (PDF)
                      </label>
                      <div className="custom-file-container border rounded p-3">
                        <input
                          type="file"
                          className="form-control"
                          accept=".pdf"
                          onChange={(e) => handleFileChange("dob", e)}
                          disabled={isUploading}
                        />
                        {(dobFile || student?.docs?.dob) && (
                          <div className="mt-2">
                            <a
                              href={
                                dobFile
                                  ? URL.createObjectURL(dobFile)
                                  : `${API_CONFIG.BASE_URL}students/download-dob/${student?.docs?.dob}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary text-decoration-none"
                            >
                              <i className="ti ti-file-text me-2"></i>
                              {dobFile ? dobFile.name : "View Document"}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex justify-content-end gap-3 mt-5">
                      <Link to={routes.studentList} className="btn btn-light">
                        Cancel
                      </Link>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleUpload}
                        disabled={isUploading || showCropper}
                      >
                        {isUploading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            ></span>
                            Uploading...
                          </>
                        ) : (
                          "Upload Documents"
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDocuments;
